import clsx from 'clsx';
import React, { useEffect, useState } from 'react';
import PanelBlockAction from './PanelBlockAction';
import { useOuterClick } from '../../../hooks/useOuterClick';
import { useIsMount } from '../../../hooks/useIsMountHook';

import { validParameterRegex } from '../../../blazeData/blazeDataUtils';
import _ from 'lodash';
import Autofocus from '../../forms/Autofocus/Autofocus';
import { copyToClipboard } from '../../buttons/CopyToClipboardButton';

export type GlobalParam = {
  name?: string;
  value?: string;
};

interface DataParamItemProps {
  isNew: boolean;
  onDelete: () => void;
  param: GlobalParam;
  onSelect?: () => void;
  isChecked: boolean;
  onSave: (param: GlobalParam) => void;
  /** Existing global params, for validation of single field. */
  existingParams: string[];
}

type FocusSource = 'name' | 'value';

const DataParamItem = (props: DataParamItemProps) => {
  const [isEditing, setIsEditing] = useState(props.isNew);
  const [param, setParam] = useState<GlobalParam>(props.param);
  const [isValid, setIsValid] = useState(!_.isNil(props.param.name) || props.isNew);
  const [focusSource, setFocusSource] = useState<FocusSource>('name');
  const [validationMessage, setValidationMessage] = useState(null);
  const isMount = useIsMount();

  /**
   * Checks if name of the parameter is valid.
   */
  const isNameValid = () => {
    const { name } = param;
    const isEmpty = _.isNil(name);
    const matchesRegex = !!name.match(validParameterRegex);
    const alreadyExists = props.existingParams.findIndex(item => item === param.name) !== -1;

    let isValid = true;
    if (!isEmpty && !matchesRegex) {
      isValid = false;
      setValidationMessage(
        'Incorrect format of parameter name. Use alphanumeric characters' +
          ' or _. Parameter name cannot begin with number.',
      );
    } else if (!isEmpty && alreadyExists) {
      isValid = false;
      setValidationMessage('Parameter name already exist in Global Scope');
    }

    return isValid;
  };

  /**
   * Validates name.
   */
  const validate = () => {
    setIsValid(isNameValid());
  };

  /**
   * Kicks in any time parameter value changes.
   */
  useEffect(() => {
    if (!isMount) {
      validate();
    }
  }, [param]);

  const innerRef = useOuterClick(e => {
    if (isEditing && !isMount && isNameValid()) {
      setIsEditing(false);
      props.onSave(sanitizeParam(param));
    }
  });

  /**
   * In case value is empty, we need to fill it with blank string otherwise
   * it will  be ignored by the backend.
   * */
  const sanitizeParam = param => {
    if (_.isNil(param.value)) {
      param.value = '';
    }
    return param;
  };

  const onEditHandler = (e, source: 'name' | 'value') => {
    e.stopPropagation();
    setFocusSource(source);
    setIsEditing(!isEditing);
  };

  const blockActions = (
    <div className="panel-block-actions">
      {!isEditing && (
        <PanelBlockAction
          title={'Copy Parameter Name to Clipboard'}
          icon={'icon-bzm-ct-copy-to-clipboard'}
          onClick={e => {
            e.stopPropagation();
            const msg = `Global Parameter \"${param.name}\" copied to clipboard. Use it in Virtual Service as a variable in state.`;
            const paramToCopy = `\${${param.name}}`;
            copyToClipboard(paramToCopy, msg);
          }}
        />
      )}

      {!isEditing && (
        <PanelBlockAction title={'Edit Parameter'} icon={'icon-bzm-edit'} onClick={e => onEditHandler(e, 'name')} />
      )}

      <PanelBlockAction title={'Delete Parameter'} icon={'icon-bzm-delete'} onClick={props.onDelete} />
    </div>
  );

  const getInlineValidation = () => {
    if (isValid === true) {
      return;
    }

    return (
      <div title={validationMessage} className="invalid-feedback advanced-editor-error-ellipsis">
        {validationMessage}
      </div>
    );
  };

  const nameInput = (
    <input
      placeholder="Enter Parameter Name ..."
      data-item="name"
      className="form-control"
      value={param.name}
      onBlur={() => validate()}
      onChange={e => {
        setParam({ ...param, name: e.target.value });
      }}
    />
  );

  const editParamField = (
    <form className={'form-inline'}>
      <div className="flex-grow-1 mx-2 panel-block-name-input-group input-group">
        <div className="input-group-prepend">
          <span className="input-group-text">{'${'}</span>
        </div>
        {focusSource === 'name' ? <Autofocus>{nameInput}</Autofocus> : nameInput}
        <div className="input-group-append">
          <span className="input-group-text">{'}'}</span>
        </div>
      </div>

      {blockActions}

      {/*Validation msg */}
      {getInlineValidation()}
    </form>
  );

  const readOnlyParamField = (
    <>
      <input checked={props.isChecked} type="checkbox" className="mr-3" data-item="select" onChange={props.onSelect} />
      <span className="panel-block-name cursor-pointer" data-item="name" onClick={e => onEditHandler(e, 'name')}>
        {'${'}
        {param.name}
        {'}'}
      </span>
      <div className="float-right">{blockActions}</div>
    </>
  );

  const editValueField = (
    <>
      <textarea
        rows={3}
        placeholder="Initial Parameter Value..."
        aria-required="true"
        title=""
        data-item="value"
        id="parameter-value-edit"
        className="bzct_ui_comp__form-control"
        onChange={e => {
          setParam({ ...param, value: e.target.value });
        }}
        value={param.value}
      />
      <span className="text-muted mt-2 d-block" style={{ whiteSpace: 'normal' }}>
        Optional initial parameter value is used during the initialization and it will be changed by state update
        action.
      </span>
    </>
  );

  const emptyParamMsg = 'Parameter value will be provided by a State Update Processing Action in the transaction.';
  return (
    <div
      className={clsx(
        isEditing ? 'panel-block-editing' : undefined,
        'panel-block panel-block-light panel-block-parameter cursor-pointer',
      )}
      ref={innerRef}
    >
      <div className="panel-block-heading">{isEditing ? editParamField : readOnlyParamField}</div>
      <div>
        <span className="panel-block-label">Parameter Value </span>
        <span className="panel-block-value" data-item="value">
          {isEditing ? (
            focusSource === 'value' ? (
              <Autofocus>{editValueField}</Autofocus>
            ) : (
              <>{editValueField}</>
            )
          ) : (
            <span
              title={_.isEmpty(param.value) ? emptyParamMsg : undefined}
              onClick={e => onEditHandler(e, 'value')}
              className="cursor-pointer"
            >
              <span>{_.isEmpty(param.value) ? <i className={'text-muted'}>{emptyParamMsg}</i> : param.value}</span>
            </span>
          )}
        </span>
      </div>
    </div>
  );
};

export default DataParamItem;
